import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import TimelineGrid from "@/components/TimelineGrid";
import { SkeletonCard } from "@/components/Skeleton";
import EmptyState from "@/components/EmptyState";
import api from "@/lib/api";
import { buildCaseColorMap } from "@/lib/caseColor";
import { Calendar as CalIcon, Home, ArrowLeft, Eye, EyeOff } from "lucide-react";

/**
 * AllCasesCalendar
 *
 * Cross-case calendar view for renters managing more than one tenancy
 * (and for Pro / advocate users juggling client matters). Pulls every
 * timeline event across the user's cases from /api/timeline/all and
 * paints chips colour-coded by case, with a toggle sidebar to show or
 * hide each case.
 *
 * Clicking a chip opens the case it came from at the timeline tab.
 */
export default function AllCasesCalendar() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState([]);
  const [items, setItems] = useState([]);
  const [hidden, setHidden] = useState(() => new Set());

  useEffect(() => {
    api
      .get("/timeline/all")
      .then(({ data }) => {
        setCases(data.cases || []);
        setItems(data.items || []);
      })
      .finally(() => setLoading(false));
  }, []);

  const caseColors = useMemo(() => buildCaseColorMap(cases), [cases]);
  const visibleItems = useMemo(
    () => items.filter((it) => !hidden.has(it.case_id)),
    [items, hidden]
  );

  // Build a filtered color map that only shows visible cases in the legend
  const visibleCaseColors = useMemo(() => {
    const m = new Map();
    for (const c of cases) {
      if (!hidden.has(c.id)) m.set(c.id, caseColors.get(c.id));
    }
    return m;
  }, [cases, hidden, caseColors]);

  const toggleCase = (cid) => {
    setHidden((h) => {
      const next = new Set(h);
      if (next.has(cid)) next.delete(cid);
      else next.add(cid);
      return next;
    });
  };

  const handleItemClick = (it) => {
    if (!it.case_id) return;
    navigate(`/cases/${it.case_id}?tab=timeline`);
  };

  return (
    <div className="min-h-screen paper-grain">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
        <div className="mb-8">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-1.5 text-[12px] text-[#1B6C62] hover:underline font-mono uppercase tracking-[0.1em] mb-4"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft size={12} strokeWidth={1.75} /> Back to your cases
          </Link>
          <div className="label-mono mb-2 flex items-center gap-1.5 text-[#1B6C62]">
            <CalIcon size={11} strokeWidth={1.75} /> Across all cases
          </div>
          <h1 className="font-serif text-4xl text-[#1A1A1A] mb-2">Calendar</h1>
          <p className="text-[14px] text-[#5a6b7a] max-w-2xl">
            Every event from every tenancy in one grid. Each case has its own
            colour so you can see overlaps at a glance.
          </p>
        </div>

        {loading ? (
          <div className="grid lg:grid-cols-[240px_1fr] gap-8">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : cases.length === 0 ? (
          <EmptyState
            icon={Home}
            title="No cases yet"
            description="Create your first case to start building a timeline."
            actionLabel="Go to dashboard"
            onAction={() => navigate("/dashboard")}
            testid="empty-state"
          />
        ) : items.length === 0 ? (
          <EmptyState
            icon={CalIcon}
            title="Nothing on the calendar yet"
            description="Once you add documents with dates or timeline events to your cases, they appear here."
            actionLabel="Open a case"
            onAction={() => navigate("/dashboard")}
            testid="empty-events"
          />
        ) : (
          <div className="grid lg:grid-cols-[240px_1fr] gap-8">
            {/* Case toggle sidebar */}
            <aside data-testid="case-toggle-sidebar">
              <div className="label-mono mb-3 text-[#0D2B52]">Cases on view</div>
              <ul className="space-y-1.5">
                {cases.map((c) => {
                  const col = caseColors.get(c.id);
                  const isHidden = hidden.has(c.id);
                  const count = items.filter((it) => it.case_id === c.id).length;
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => toggleCase(c.id)}
                        className={`w-full text-left flex items-start gap-2 px-2.5 py-2 border transition-colors ${
                          isHidden
                            ? "border-[#E5E4DE] bg-transparent opacity-60"
                            : "border-[#E5E4DE] bg-white hover:bg-[#FAF9F6]"
                        }`}
                        data-testid={`toggle-case-${c.id}`}
                        aria-pressed={!isHidden}
                      >
                        <span
                          className="w-2.5 h-2.5 mt-1 shrink-0"
                          style={{ background: col?.stripe }}
                          aria-hidden="true"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-[12px] text-[#0D2B52] font-medium truncate leading-tight">
                            {c.title}
                          </div>
                          <div className="font-mono text-[9.5px] text-[#6B7280] mt-0.5">
                            {c.state} · {count} event{count === 1 ? "" : "s"}
                          </div>
                        </div>
                        {isHidden ? (
                          <EyeOff size={12} strokeWidth={1.5} className="text-[#9CA3AF] shrink-0 mt-0.5" />
                        ) : (
                          <Eye size={12} strokeWidth={1.5} className="text-[#1B6C62] shrink-0 mt-0.5" />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-4 font-mono text-[10px] text-[#9CA3AF] leading-snug">
                Click a case to hide its chips. Click again to show them.
              </div>
            </aside>

            {/* Grid */}
            <div data-testid="all-cases-grid">
              <TimelineGrid
                items={visibleItems}
                caseColors={visibleCaseColors}
                showCaseLegend
                onItemClick={handleItemClick}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
